import * as React from 'react';
import { copyArrayWithIndexChanged } from '../../../Utils';
import { GridColDef, GridRowModel } from '@mui/x-data-grid';
import { Autocomplete, Box, TextField, FormControl } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { WorkshopUser, WorkshopUserStatusStrings, deleteWorkshopUser, getWorkshopUsers, updateWorkshopUser } from '../../../types/WorkshopUser';
import { UserAccessLevelStrings, User, getDuplicateUsers, getUserWorkshops } from '../../../types/User';
import { EditableTable } from '../../../subs/EditableTable';
import { SuccessFailureIndicator } from '../../../subs/SuccessFailureIndicator';
import { Workshop, getWorkshops } from '../../../types/Workshop';

import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../../../contexts/SnackBarContext';

export const WorkshopUsersAdminView = () => {
    const { showSnackbar } = useSnackbar();
    const [workshops, setWorkshops] = React.useState<Workshop[]>([]);
    const [workshop, setWorkshop] = React.useState<Workshop>();
    const [duplicateUsers, setDuplicateUsers] = React.useState<User[]>([]);
    const [selectedUser, setSelectedUser] = React.useState<User>();
    const [allData, setallData] = React.useState<WorkshopUser[]>([]);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetchAllWorkshops = async () => {
            const allworkshopsResponse = await getWorkshops(new AbortController());
            if (allworkshopsResponse.failed) {
                showUserFeedback(allworkshopsResponse.errorText);
                return;
            }
            setWorkshops(allworkshopsResponse.result);

            const allDuplicateUsersResponse = await getDuplicateUsers(new AbortController());
            if (allDuplicateUsersResponse.failed) {
                showUserFeedback(allDuplicateUsersResponse.errorText);
                return;
            }
            setDuplicateUsers(allDuplicateUsersResponse.result);
        }
        fetchAllWorkshops();
    }, [showUserFeedback]);

    React.useEffect(() => {
        const fetchAllWorkshopUsers = async () => {
            if (workshop === undefined || workshop.id === 0) return;
            const wsUsersResponse = await getWorkshopUsers(workshop.id, new AbortController());
            if (wsUsersResponse.failed) {
                showUserFeedback(wsUsersResponse.errorText);
                return;
            }
            setallData(wsUsersResponse.result);
        }
        fetchAllWorkshopUsers();
    }, [workshop, showUserFeedback]);

    React.useEffect(() => {
        const fetchAllWorkshopUsers = async () => {
            if (selectedUser === undefined || selectedUser.id === undefined || selectedUser.id === 0) return;
            const wsUsersResponse = await getUserWorkshops(selectedUser.id, new AbortController());
            if (wsUsersResponse.failed) {
                showUserFeedback(wsUsersResponse.errorText);
                return;
            }
            setallData(wsUsersResponse.result);
        }
        fetchAllWorkshopUsers();
    }, [selectedUser, showUserFeedback]);

    const processRowUpdate = (newRow: GridRowModel) => {
        const changedData = allData.find(row => row.id === newRow.id);
        if (!changedData) {
            showUserFeedback('Row not found', 'error');
            return newRow;
        }
        changedData.status = newRow.status as number;
        changedData.isWorkshopAdmin = newRow.isWorkshopAdmin as boolean;
        changedData.comment = newRow.comment as string;
        saveHandler(changedData);
        return newRow;
    };

    const saveHandler = async (changedData: WorkshopUser) => {
        const updateUserRequest = await updateWorkshopUser(changedData, new AbortController());

        if (updateUserRequest.failed) {
            showUserFeedback(updateUserRequest.errorText);
            return;
        }
        const updateUser = updateUserRequest.result;
        let allUsersLoc = copyArrayWithIndexChanged(allData, allData.findIndex(tc => tc.id === updateUser.id), updateUser);
        setallData(allUsersLoc);
    };

    const processDelete = async (id: number) => {
        const deleteUserRequest = await deleteWorkshopUser(id, new AbortController());

        if (deleteUserRequest.failed) {
            showUserFeedback(deleteUserRequest.errorText);
            return;
        }
        let allUsersLoc = allData.filter(c => c.id !== id);
        setallData(allUsersLoc);
    };

    const columns: GridColDef[] = [
        {
            field: 'guid',
            headerName: 'GUID',
            flex: 2
        },
        {
            field: 'userId',
            headerName: 'User ID',
            flex: 1,
        },
        {
            field: 'workshopId',
            headerName: 'Workshop ID',
            flex: 1,
        },
        {
            field: 'companyId',
            headerName: 'Company ID',
            flex: 1,
        },
        {
            field: 'accessLevel',
            headerName: 'Access Level',
            flex: 1,
            editable: true,
            valueFormatter: (value?: number) => {
                if (value == null) {
                    return '';
                }
                return UserAccessLevelStrings[value];
            },
            valueGetter: (value, row) => {
                if (value == null) {
                    return '';
                }
                return UserAccessLevelStrings[value];
            },
            valueSetter: (value, row) => {
                let accessLevel = 0;
                if (UserAccessLevelStrings.find(v => v === value) !== null) {
                    for (let i = 0; i < UserAccessLevelStrings.length; i++) {
                        if (UserAccessLevelStrings[i] === value) {
                            accessLevel = i;
                            break;
                        }
                    }
                }
                return { ...row, accessLevel: accessLevel };
            },
            type: 'singleSelect',
            valueOptions: UserAccessLevelStrings
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            editable: true,
            valueFormatter: (value?: number) => {
                if (value == null) {
                    return '';
                }
                return WorkshopUserStatusStrings[value];
            },
            valueGetter: (value, row) => {
                if (value == null) {
                    return '';
                }
                return WorkshopUserStatusStrings[value];
            },
            valueSetter: (value, row) => {
                let status = 0;
                if (WorkshopUserStatusStrings.find(v => v === value) !== null) {
                    for (let i = 0; i < WorkshopUserStatusStrings.length; i++) {
                        if (WorkshopUserStatusStrings[i] === value) {
                            status = i;
                            break;
                        }
                    }
                }
                return { ...row, status: status };
            },
            type: 'singleSelect',
            valueOptions: WorkshopUserStatusStrings
        },
        {
            field: 'departureTime',
            headerName: 'Departure Time',
            flex: 1,
            editable: true,
        },
        {
            field: 'arrivalTime',
            headerName: 'Arrival Time',
            flex: 1,
            editable: true,
        },
        {
            field: 'hotel',
            headerName: 'Hotel',
            flex: 1,
            editable: true,
        },
        {
            field: 'comment',
            headerName: 'Comment',
            flex: 2,
            editable: true,
        },
        {
            field: 'isWorkshopAdmin',
            headerName: 'Is Workshop Admin',
            type: 'boolean',
            flex: 1,
            editable: true,
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.isWorkshopAdmin} /> },
        },
        {
            field: 'id'
        },
    ];

    return <React.Fragment >
        <Grid size={6} paddingBottom={10}>
            <FormControl required fullWidth>
                <Autocomplete id='ac-wsOptions' options={workshops.map(w => w.name)}
                    value={workshop ? workshop.name : ''}
                    onChange={(e, value) => setWorkshop(workshops.find(w => w.name === value))}
                    renderInput={(params) =>
                        <TextField name='wsOptions' multiline
                            {...params}
                            label={"Workshop"}
                            sx={{
                                '& .MuiOutlinedInput-root fieldset': {
                                    borderColor: 'divider',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                    borderColor: 'divider',
                                },
                            }}
                        />
                    }
                />
            </FormControl>
        </Grid>
        <Grid size={6} paddingBottom={10}>
            <FormControl required fullWidth>
                <Autocomplete id='ac-wsdupUser' options={duplicateUsers.map(u => u.email)}
                    value={selectedUser ? selectedUser.email : ''}
                    onChange={(e, value) => setSelectedUser(duplicateUsers.find(u => u.email === value))}
                    renderInput={(params) =>
                        <TextField name='wsdupUser' multiline
                            {...params}
                            label={"User"}
                            sx={{
                                '& .MuiOutlinedInput-root fieldset': {
                                    borderColor: 'divider',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                    borderColor: 'divider',
                                },
                            }}
                        />
                    }
                />
            </FormControl>
        </Grid>
        {
            workshop &&
            <Box sx={{ flexGrow: 1, width: "100%" }} style={{ overflow: 'auto' }}>
                <EditableTable
                    columns={columns}
                    rows={allData}
                    setAllRows={setallData}
                    processRowUpdate={processRowUpdate}
                    processRowDelete={processDelete}
                />
            </Box>
        }
    </React.Fragment >
}